/**
 * Architecture Comparison Utility
 *
 * Compares the output of the old makeLayout implementation with the new
 * transform-based architecture to ensure parity during migration.
 */

import {Layout, MediaConfigWithDefaults} from '../../Component';
import {makeLayout} from '../../makeLayout';
import {
  getMaskOptions,
  getTilesWithMaskOptions,
} from '../../tiles/getTilesWithMaskOptions';
import {StepMeta} from '../../types';
import {TransformInput} from '../types';
import {adaptGridDataToLayout} from './gridDataAdapter';
import {dispatchTransform} from './transformDispatcher';

/**
 * Input structure for comparison.
 */
type ComparisonInput = {
  /** Runs with media data */
  runs: TransformInput['runs'];
  /** Step metadata */
  stepMeta: StepMeta;
  /** Current step/slider value */
  currentStep: number;
  /** Current media index */
  currentIndex: number;
  /** Panel configuration */
  config: MediaConfigWithDefaults;
  /** Mask data */
  allMasks: TransformInput['allMasks'];
};

/**
 * Comparison result structure.
 */
type ComparisonResult = {
  /** Whether the outputs match */
  match: boolean;
  /** Old architecture output */
  oldLayout: Layout;
  /** New architecture output */
  newLayout: Layout;
  /** List of differences found */
  differences: string[];
  /** Detailed comparison stats */
  stats: {
    oldTileCount: number;
    newTileCount: number;
    tileCountMatch: boolean;
    axisDataMatch: boolean;
  };
};

/**
 * Compares old and new layout architectures.
 *
 * @param input - Comparison input
 * @returns Comparison result with differences
 *
 * @example
 * ```typescript
 * const result = compareArchitectures({
 *   runs: [run1, run2],
 *   stepMeta,
 *   currentStep: 50,
 *   currentIndex: 0,
 *   config: { mode: 'gallery', ... },
 *   allMasks: {}
 * });
 *
 * if (!result.match) {
 *   console.error('Architectures differ:', result.differences);
 * }
 * ```
 */
export function compareArchitectures(input: ComparisonInput): ComparisonResult {
  const differences: string[] = [];

  // Generate layout using OLD architecture
  const oldLayout = makeLayout({
    runs: input.runs,
    stepMeta: input.stepMeta,
    config: input.config,
    allMasks: input.allMasks,
    currentStep: input.currentStep,
  });

  // Expand old layout tiles with mask options (same as PanelTileGroup does)
  // Handle groups by expanding tiles within groups
  const oldTilesExpanded: any[] = [];
  for (const tileOrGroup of oldLayout.tiles) {
    if ('isEmpty' in tileOrGroup && tileOrGroup.isEmpty) {
      oldTilesExpanded.push(tileOrGroup);
      continue;
    }
    if ('groupType' in tileOrGroup && 'items' in tileOrGroup) {
      // Expand tiles within the group
      const expandedGroupTiles = getTilesWithMaskOptions({
        config: input.config,
        allMasks: input.allMasks,
        tiles: tileOrGroup.items.filter(
          (t): t is Exclude<typeof t, {isEmpty: true}> =>
            !('isEmpty' in t && t.isEmpty)
        ),
      });
      oldTilesExpanded.push({
        ...tileOrGroup,
        items: expandedGroupTiles,
      });
    } else {
      // Single tile - expand it
      const expandedTiles = getTilesWithMaskOptions({
        config: input.config,
        allMasks: input.allMasks,
        tiles: [tileOrGroup],
      });
      oldTilesExpanded.push(...expandedTiles);
    }
  }
  const oldLayoutExpanded = {
    ...oldLayout,
    tiles: oldTilesExpanded,
  };

  // Generate layout using NEW architecture
  let newLayout: Layout;
  try {
    const transformInput: TransformInput = {
      runs: input.runs,
      stepMeta: input.stepMeta,
      currentStep: input.currentStep,
      currentIndex: input.currentIndex,
      config: input.config,
      allMasks: input.allMasks,
      allBoxes: {},
    };

    const gridData = dispatchTransform(transformInput);
    newLayout = adaptGridDataToLayout(gridData);

    // Expand new layout tiles with mask options (same as GridRenderer does)
    const newTilesExpanded: Layout['tiles'] = [];
    for (const tile of newLayout.tiles) {
      if ('isEmpty' in tile && tile.isEmpty) {
        newTilesExpanded.push(tile);
      } else if ('groupType' in tile && 'items' in tile) {
        // Handle groups (though new layout shouldn't have groups, handle for safety)
        newTilesExpanded.push(tile);
      } else {
        // Expand single tile with mask options
        const maskOptions = getMaskOptions(input.config, input.allMasks, tile);
        for (const mo of maskOptions) {
          newTilesExpanded.push({...tile, maskOptions: mo} as any);
        }
      }
    }
    newLayout = {
      ...newLayout,
      tiles: newTilesExpanded,
    };
  } catch (error) {
    differences.push(
      `New architecture threw error: ${(error as Error).message}`
    );
    newLayout = {tiles: [], galleryAxisData: null, gridAxisData: null};
  }

  // Compare tile counts (after expansion)
  // Count actual tiles that will be rendered (flatten groups)
  const countRenderedTiles = (tiles: Layout['tiles']): number => {
    return tiles.reduce((count, tileOrGroup) => {
      if ('isEmpty' in tileOrGroup && tileOrGroup.isEmpty) {
        return count + 1; // Empty tiles still count as 1
      }
      if ('groupType' in tileOrGroup && 'items' in tileOrGroup) {
        return count + tileOrGroup.items.length;
      }
      return count + 1;
    }, 0);
  };

  const oldTileCount = countRenderedTiles(oldLayoutExpanded.tiles);
  const newTileCount = countRenderedTiles(newLayout.tiles);
  const tileCountMatch = oldTileCount === newTileCount;

  if (!tileCountMatch) {
    differences.push(
      `Tile count mismatch: old=${oldTileCount}, new=${newTileCount}`
    );
  }

  // Compare axis data
  const axisDataMatch = compareAxisData(
    oldLayoutExpanded,
    newLayout,
    differences
  );

  // Compare individual tiles (if counts match)
  if (tileCountMatch) {
    compareTiles(oldLayoutExpanded.tiles, newLayout.tiles, differences);
  }

  return {
    match: differences.length === 0,
    oldLayout: oldLayoutExpanded,
    newLayout,
    differences,
    stats: {
      oldTileCount,
      newTileCount,
      tileCountMatch,
      axisDataMatch,
    },
  };
}

/**
 * Compares axis data between old and new layouts.
 */
function compareAxisData(
  oldLayout: Layout,
  newLayout: Layout,
  differences: string[]
): boolean {
  let match = true;

  // Compare gallery axis data
  if (
    JSON.stringify(oldLayout.galleryAxisData) !==
    JSON.stringify(newLayout.galleryAxisData)
  ) {
    differences.push(
      `Gallery axis data mismatch: old=${JSON.stringify(oldLayout.galleryAxisData)}, new=${JSON.stringify(newLayout.galleryAxisData)}`
    );
    match = false;
  }

  // Compare grid axis data
  if (
    JSON.stringify(oldLayout.gridAxisData) !==
    JSON.stringify(newLayout.gridAxisData)
  ) {
    differences.push(
      `Grid axis data mismatch: old=${JSON.stringify(oldLayout.gridAxisData)}, new=${JSON.stringify(newLayout.gridAxisData)}`
    );
    match = false;
  }

  return match;
}

/**
 * Flattens tiles (expanding groups) and returns a flat array of non-empty tiles.
 */
function flattenTiles(tiles: Layout['tiles']): Array<{
  mediaKey: string;
  mediaStep: number;
  mediaIndex: number;
  runSignature: {entityName: string; projectName: string; runName: string};
}> {
  const result: Array<{
    mediaKey: string;
    mediaStep: number;
    mediaIndex: number;
    runSignature: {entityName: string; projectName: string; runName: string};
  }> = [];

  for (const tileOrGroup of tiles) {
    if ('isEmpty' in tileOrGroup && tileOrGroup.isEmpty) {
      continue; // Skip empty tiles
    }
    if ('groupType' in tileOrGroup && 'items' in tileOrGroup) {
      // Expand groups
      for (const item of tileOrGroup.items) {
        if ('isEmpty' in item && item.isEmpty) {
          continue;
        }
        result.push({
          mediaKey: item.mediaKey,
          mediaStep: item.mediaStep,
          mediaIndex: item.mediaIndex,
          runSignature: item.runSignature,
        });
      }
    } else {
      // Single tile
      result.push({
        mediaKey: tileOrGroup.mediaKey,
        mediaStep: tileOrGroup.mediaStep,
        mediaIndex: tileOrGroup.mediaIndex,
        runSignature: tileOrGroup.runSignature,
      });
    }
  }

  return result;
}

/**
 * Creates a sort key for a tile to ensure consistent ordering.
 */
function getTileSortKey(tile: {
  mediaKey: string;
  mediaStep: number;
  mediaIndex: number;
  runSignature: {entityName: string; projectName: string; runName: string};
}): string {
  // Sort by: runSignature (entity, project, run), then mediaKey, then mediaStep, then mediaIndex
  return `${tile.runSignature.entityName}|${tile.runSignature.projectName}|${tile.runSignature.runName}|${tile.mediaKey}|${tile.mediaStep}|${tile.mediaIndex}`;
}

/**
 * Compares individual tiles between old and new layouts.
 * Tiles are sorted before comparison to handle different ordering.
 */
function compareTiles(
  oldTiles: Layout['tiles'],
  newTiles: Layout['tiles'],
  differences: string[]
): void {
  // Flatten and sort tiles for comparison
  const oldTilesFlat = flattenTiles(oldTiles);
  const newTilesFlat = flattenTiles(newTiles);

  // Log first few tiles before sorting for debugging
  if (oldTilesFlat.length > 0) {
    console.log(
      '[compareArchitectures] First 5 old tiles (before sort):',
      oldTilesFlat.slice(0, 5).map(t => ({
        run: t.runSignature.runName,
        mediaKey: t.mediaKey,
        step: t.mediaStep,
        index: t.mediaIndex,
      }))
    );
    console.log(
      '[compareArchitectures] First 5 new tiles (before sort):',
      newTilesFlat.slice(0, 5).map(t => ({
        run: t.runSignature.runName,
        mediaKey: t.mediaKey,
        step: t.mediaStep,
        index: t.mediaIndex,
      }))
    );
  }

  const oldTilesSorted = oldTilesFlat.sort((a, b) =>
    getTileSortKey(a).localeCompare(getTileSortKey(b))
  );
  const newTilesSorted = newTilesFlat.sort((a, b) =>
    getTileSortKey(a).localeCompare(getTileSortKey(b))
  );

  if (oldTilesSorted.length !== newTilesSorted.length) {
    differences.push(
      `Tile count after flattening: old=${oldTilesSorted.length}, new=${newTilesSorted.length}`
    );
    return;
  }

  // Compare sorted tiles
  for (let i = 0; i < oldTilesSorted.length; i++) {
    const oldTile = oldTilesSorted[i];
    const newTile = newTilesSorted[i];

    // Compare essential properties
    if (oldTile.mediaKey !== newTile.mediaKey) {
      differences.push(
        `Tile ${i}: mediaKey mismatch: old=${oldTile.mediaKey}, new=${newTile.mediaKey}`
      );
    }

    if (oldTile.mediaStep !== newTile.mediaStep) {
      differences.push(
        `Tile ${i}: mediaStep mismatch: old=${oldTile.mediaStep}, new=${newTile.mediaStep}`
      );
    }

    if (oldTile.mediaIndex !== newTile.mediaIndex) {
      differences.push(
        `Tile ${i}: mediaIndex mismatch: old=${oldTile.mediaIndex}, new=${newTile.mediaIndex}`
      );
    }

    // Compare run signatures
    if (
      oldTile.runSignature.runName !== newTile.runSignature.runName ||
      oldTile.runSignature.entityName !== newTile.runSignature.entityName ||
      oldTile.runSignature.projectName !== newTile.runSignature.projectName
    ) {
      differences.push(
        `Tile ${i}: runSignature mismatch: old=${JSON.stringify(oldTile.runSignature)}, new=${JSON.stringify(newTile.runSignature)}`
      );
    }
  }
}

/**
 * Logs a comparison result in a readable format.
 *
 * @param result - Comparison result
 * @param label - Optional label for the comparison
 */
export function logComparisonResult(
  result: ComparisonResult,
  label?: string
): void {
  const prefix = label ? `[${label}] ` : '';

  if (result.match) {
    console.log(`${prefix}✅ Architectures match perfectly!`);
    console.log(`${prefix}  Tile count: ${result.stats.oldTileCount} (both)`);
  } else {
    console.error(`${prefix}❌ Architectures differ:`);
    console.error(`${prefix}  Old tile count: ${result.stats.oldTileCount}`);
    console.error(`${prefix}  New tile count: ${result.stats.newTileCount}`);
    console.error(`${prefix}  Differences:`);
    result.differences.forEach(diff => {
      console.error(`${prefix}    - ${diff}`);
    });
  }
}

/**
 * Asserts that architectures match (for use in tests).
 *
 * @param result - Comparison result
 * @throws Error if architectures don't match
 */
export function assertArchitecturesMatch(result: ComparisonResult): void {
  if (!result.match) {
    const message = [
      'Architectures do not match:',
      ...result.differences.map(d => `  - ${d}`),
    ].join('\n');
    throw new Error(message);
  }
}
